import {
  SIDEBAR_WIDTH_COLLAPSED,
  SIDEBAR_WIDTH_EXPANDED,
  useSidebarState,
} from '@src/hooks/useSidebarState';
import { useDatoPerson, useUserIdentity } from '@src/stores-v2/StoreContext';
import ToggleSidebarIcon from '@src/svgs/ToggleSidebarIcon';
import { motion as fmMotion } from 'framer-motion';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { useVideoCreatorStore } from '../stores-v2/VideoCreatorStoreContext';
import { getCurrentStudio } from '../utility/general';
import { useFlagsCombination } from '../utility/useFlagsCombination';
import MainMenuNavList from './MainMenuNavList';
import MultiOrganizationAvatar from './MultiOrganizationAvatar';
import { SIDEBAR_SPRING } from './sidebar/sidebarMotion';
import SidebarSetupSection from './sidebar/SidebarSetupSection';
import SidebarUpdateCard from './sidebar/SidebarUpdateCard';
import { SidebarActiveStoryCard } from './SidebarActiveStoryCard';
import { SidebarTooltip } from './SidebarTooltip';
import SingleOrganizationAvatar from './SingleOrganizationAvatar';
import WhatsNewButton from './WhatsNew/WhatsNewButton';

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
const MainMenu = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const userIdentity = useUserIdentity();
  const datoPerson = useDatoPerson();
  const { pathname, search } = useLocation();
  const { collapsed, showLabels, toggle } = useSidebarState();

  const {
    hideStoryDashboardNav,
    enableWhatsNewChangelog,
    enableSettingsScreen,
    enableSpeakerManager,
    enableTranslationReviewerMode,
  } = useFlagsCombination(userIdentity.currentRole);
  const album = videoCreator.organization;
  const currentStudio = getCurrentStudio(pathname);
  const allowStoryManager = !hideStoryDashboardNav && !!album;
  // Translation reviewers keep only Story Manager; Home, Speakers, Settings,
  // and Brand Kit are hidden.
  const allowHome = !enableTranslationReviewerMode;
  const allowSpeakerManager =
    enableSpeakerManager && !!album && !enableTranslationReviewerMode;
  const allowSettings = enableSettingsScreen && !enableTranslationReviewerMode;

  /* Only show the Current Story section if there's an actual story context:
   * either a loaded story or a storyId in the URL (= a story is being
   * fetched). On routes like /story-manager that only carry showcase=,
   * we'd otherwise render "Loading Story" forever. */
  const hasStoryIdInUrl = !!new URLSearchParams(search).get('storyId');
  const showStoryCard = !!videoCreator.story || hasStoryIdInUrl;

  if (!currentStudio) return null;

  const targetWidth = collapsed
    ? SIDEBAR_WIDTH_COLLAPSED
    : SIDEBAR_WIDTH_EXPANDED;

  const avatarLogoSize = collapsed ? 28 : 32;

  const sidebarBody = (
    <>
      <LogoSection $collapsed={collapsed}>
        <LogoLink to={`/home${window.location.search}`}>
          {/* Single wordmark, clipped to show just the "a" when collapsed.
           * Wrapper width animates between collapsed (≈ width of "a")
           * and expanded (full wordmark width) with SIDEBAR_SPRING. */}
          <LogoReveal
            animate={{
              width: showLabels ? LOGO_WIDTH_EXPANDED : LOGO_WIDTH_COLLAPSED,
            }}
            initial={false}
            transition={SIDEBAR_SPRING}
          >
            <ArborLogo>
              <img src="/logo/arbor_logo.png" alt="Arbor" />
            </ArborLogo>
          </LogoReveal>
        </LogoLink>
        <ToggleButton
          onClick={toggle}
          aria-label={collapsed ? 'Open Sidebar' : 'Collapse Sidebar'}
          initial={false}
          animate={{
            x: collapsed ? -7 : 0,
            y: collapsed ? 27 : 0,
          }}
          transition={SIDEBAR_SPRING}
        >
          <ToggleSidebarIcon
            width={collapsed ? '15' : '18'}
            height={collapsed ? '15' : '18'}
          />
          {collapsed && <ToggleTooltip>Open Sidebar</ToggleTooltip>}
        </ToggleButton>
      </LogoSection>

      <MainMenuNavList
        currentStudio={currentStudio}
        collapsed={collapsed}
        allowHome={allowHome}
        allowStoryManager={allowStoryManager}
        allowSpeakerManager={allowSpeakerManager}
        search={search}
      />

      {showStoryCard && (
        <SidebarActiveStoryCard
          story={videoCreator.story ?? null}
          collapsed={collapsed}
        />
      )}

      <SidebarSetupSection
        currentStudio={currentStudio}
        collapsed={collapsed}
        allowBrandKit={!enableTranslationReviewerMode}
        allowSettings={allowSettings}
        search={search}
      />

      <SidebarSpacer />

      {enableWhatsNewChangelog && <WhatsNewButton collapsed={collapsed} />}

      <SidebarUpdateCard collapsed={collapsed} />

      {album && (
        <AvatarSection $collapsed={collapsed}>
          {datoPerson.canSwitchBetweenOrganizations() ? (
            <MultiOrganizationAvatar logoSize={avatarLogoSize} compact />
          ) : (
            <SingleOrganizationAvatar logoSize={avatarLogoSize} compact />
          )}
        </AvatarSection>
      )}
    </>
  );

  return (
    <Sidebar
      as={fmMotion.nav}
      data-testid="nav-sidebar"
      initial={false}
      animate={{ width: targetWidth }}
      transition={SIDEBAR_SPRING}
    >
      {sidebarBody}
    </Sidebar>
  );
});

export default MainMenu;

/* ------------------------------------------------------------------ */
/*  Styles                                                             */
/* ------------------------------------------------------------------ */
const Sidebar = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  background: var(--sidebar);
  border-right: 1px solid var(--sidebar-border);
  display: flex;
  flex-direction: column;
  /* PLAT-11656 — lifted above the clip preview's scrim while the Translation
     Review's clip entry is open (TOP_LAYER_VARS.sidebar); 1000002 at rest. */
  z-index: var(--top-layer-sidebar-z, 1000002);
  padding: 0;
  /* Visible so SidebarTooltips can render outside the right edge. */
  overflow: visible;
  will-change: width;
`;

const LogoSection = styled.div<{ $collapsed: boolean }>`
  /* Fixed height in both states; row flex in both states so the logo
   * stays vertically centered at y=30 regardless of sidebar width
   * (same Y-stable behavior as the Home / Story Manager icons below).
   * The ToggleButton lives outside the flex flow (position: absolute)
   * so it doesn't interact with this layout. */
  height: 60px;
  padding: ${p => (p.$collapsed ? '0 10px 0 24px' : '16px 12px 16px 20px')};
  transition: padding var(--ease-sidebar-glide);
  display: flex;
  flex-direction: row;
  align-items: center;
  box-sizing: border-box;
`;

const LogoLink = styled(NavLink)`
  text-decoration: none;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

export const ArborLogo = styled.div`
  position: relative;
  height: 18px;
  z-index: 1;
  /* The inner <img> keeps its natural aspect ratio at 18px height; the
   * parent LogoReveal wrapper animates its own width to clip/reveal. */
  img {
    height: 100%;
    width: auto;
    display: block;
  }
`;

/* Width-clip wrapper around the wordmark image. Width animates between
 * the full wordmark (expanded) and just the leading "a" (collapsed)
 * via Framer's SIDEBAR_SPRING, in sync with the sidebar width animation. */
const LOGO_WIDTH_EXPANDED = 58; /* 18px height × (253/78) aspect of arbor_logo.png */
/* Measured from arbor_logo.png pixel data: "a" body ends at original x=51,
 * "r" starts at original x=55. At 18/78 scale that's display x 11.77 → 12.69.
 * Clipping at 12px lands in the fully-transparent gap (x=12.00–12.46). */
const LOGO_WIDTH_COLLAPSED = 12;

const LogoReveal = styled(fmMotion.div)`
  height: 18px;
  overflow: hidden;
  display: flex;
  align-items: center;
`;

const ToggleTooltip = styled(SidebarTooltip)``;

const ToggleButton = styled(fmMotion.button)`
  /* Anchored absolutely to LogoSection. Base position is vertical center
   * (top: 19px = (60-22)/2). In collapsed mode, Framer animates Y by +23
   * to drop the toggle below the short logo (matching the previous
   * column-flex-bottom + translateY(8) position). The animation runs
   * with SIDEBAR_SPRING in sync with the sidebar width spring. */
  position: absolute;
  top: 19px;
  right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  transition:
    background var(--transition-fast) ease,
    color var(--transition-fast) ease;

  &:hover {
    background: var(--sidebar-active);
    color: var(--text-secondary);
  }

  &:hover ${ToggleTooltip} {
    opacity: 1;
  }
`;

const SidebarSpacer = styled.div`
  flex: 1;
`;

const AvatarSection = styled.div<{ $collapsed: boolean }>`
  position: relative;
  padding: ${p => (p.$collapsed ? '12px 8px' : '12px 14px')};
  transition: padding var(--ease-sidebar-glide);
  display: flex;
  align-items: center;
  justify-content: ${p => (p.$collapsed ? 'center' : 'flex-start')};
  overflow: visible;
  ${p =>
    p.$collapsed &&
    `
      border-radius: 8px;
      margin: 0 4px;
      padding: 10px 8px;
      cursor: pointer;
      transition:
        background 0.15s ease,
        color 0.15s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.06);
      }
    `}

  /* Override the default row layout of OrganizationAvatarWrapper.
   * Note: overflow stays visible so the avatar's glass-ring hover effect
   * isn't clipped. The text spans inside (ShowcaseName, EmailLocal) have
   * their own overflow: hidden + text-overflow: ellipsis. */
  > div {
    display: flex;
    flex-direction: ${p => (p.$collapsed ? 'column' : 'row')};
    align-items: center;
    ${p =>
      p.$collapsed
        ? `
            width: auto;
            justify-content: center;
          `
        : `
            width: 100%;
            min-width: 0;
          `}
  }

  ${p =>
    !p.$collapsed &&
    `
      [data-avatar-wrapper] {
        min-width: 0;
      }
    `}

  /* Hide the text info when collapsed (only the avatar's own info, not tooltip content) */
  ${p =>
    p.$collapsed &&
    `
      [data-avatar-wrapper] .organization-info {
        display: none;
      }
    `}

  /* Override text alignment for sidebar context */
  .organization-info {
    text-align: left;
    min-width: 0;
    flex: 1;

    .album-title {
      font-size: 13px;
    }

    .username {
      font-size: 11px;
    }
  }
`;
